import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import SpeedDial, { CloseReason } from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import AnchorIcon from "mdi-react/AnchorIcon";
import FerryIcon from "mdi-react/FerryIcon";
import WallIcon from "mdi-react/WallIcon";
import React from "react";
import { classNames } from "react-extras";
import { useTranslation } from "react-i18next";
import { useAccess } from "~/utils/hooks/useAccess";
import { usePortFocus } from "../hooks/usePortFocus";

export type ToolbarAction =
  | "addPort"
  | "addEvent"
  | "addDam"
  | "addFerry"
  | "addBerth"
  | "addPortService"
  | "addPortArrivalZone";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    speedDial: {
      position: "absolute",
      bottom: theme.spacing(2),
      left: theme.spacing(2),
    },
    tooltip: {
      whiteSpace: "nowrap",
    },
    portInFocus: {
      [theme.breakpoints.down("md")]: {
        bottom: `calc(${theme.spacing(2)} + ${52}px)`, // Snackbar 52px
      },
    },
  }),
);

interface Props {
  onActionClick: (action: ToolbarAction) => void;
  portInFocus: boolean;
}

const MapToolbar = ({
  onActionClick,
  portInFocus,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation("map");
  const { focusedPort } = usePortFocus({});
  const { PortUpdate, EventUpdate, FerryUpdate, DamUpdate } = useAccess();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = (
    _event: React.SyntheticEvent<unknown, Event>,
    reason: CloseReason,
  ) => {
    // Do not close the dialog if the button is pressed
    if (reason !== "toggle") {
      setOpen(false);
    }
  };

  const handleActionClick = (action: ToolbarAction) => {
    setOpen(false);
    onActionClick(action);
  };

  if (!PortUpdate && !EventUpdate && !FerryUpdate && !DamUpdate) return null;
  return (
    <SpeedDial
      ariaLabel="Map toolbar"
      className={classNames(classes.speedDial, {
        [classes.portInFocus]: portInFocus,
      })}
      icon={<AddIcon />}
      openIcon={<AddIcon />}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
    >
      {PortUpdate && (
        <SpeedDialAction
          key="port"
          icon={<AnchorIcon />}
          tooltipTitle={t("portDialog:addTitle")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addPort")}
        />
      )}
      {PortUpdate && focusedPort && (
        <SpeedDialAction
          key="berth"
          icon={<AnchorIcon />}
          tooltipTitle={t("berths:add-berth")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addBerth")}
        />
      )}
      {PortUpdate && focusedPort && (
        <SpeedDialAction
          key="portService"
          icon={<AnchorIcon />}
          tooltipTitle={t("arrival-zones:addNewArrivalZone")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addPortArrivalZone")}
        />
      )}
      {PortUpdate && focusedPort && (
        <SpeedDialAction
          key="portArrivalZone"
          icon={<AnchorIcon />}
          tooltipTitle={t("port-services:create-service")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addPortService")}
        />
      )}
      {EventUpdate && (
        <SpeedDialAction
          key="event"
          icon={<EventIcon />}
          tooltipTitle={t("event-dialog:add-title")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addEvent")}
        />
      )}
      {DamUpdate && (
        <SpeedDialAction
          key="dam"
          icon={<WallIcon />}
          tooltipTitle={t("dam-page:add")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addDam")}
        />
      )}
      {FerryUpdate && (
        <SpeedDialAction
          key="ferry"
          icon={<FerryIcon />}
          tooltipTitle={t("ferry-page:add")}
          classes={{ staticTooltipLabel: classes.tooltip }}
          tooltipOpen
          tooltipPlacement="right"
          onClick={() => handleActionClick("addFerry")}
        />
      )}
    </SpeedDial>
  );
};

export default MapToolbar;
